import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/AppShell';
import { EmptyState, PageLoader, TooltipProvider } from '@/components/ui/primitives';
import { ApiError } from '@/lib/api';
import { AuthProvider, useAuth } from '@/lib/auth';
import { InboxPage } from '@/pages/InboxPage';
import { LoginPage } from '@/pages/LoginPage';
import { MyDayPage } from '@/pages/MyDayPage';
import { OrganizationPage } from '@/pages/OrganizationPage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { PipelinePage } from '@/pages/PipelinePage';
import { OrdersPage, QuotesPage } from '@/pages/DocumentsPages';
import { OrderPage } from '@/pages/OrderPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { QuotePage } from '@/pages/QuotePage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { TasksPage } from '@/pages/TasksPage';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (count, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && count < 2,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<MyDayPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="leads" element={<OrganizationsPage key="leads" lifecycle="lead" />} />
        <Route path="customers" element={<OrganizationsPage key="customers" lifecycle="customer" />} />
        <Route path="org/:id" element={<OrganizationPage />} />
        <Route path="pipeline" element={<PipelinePage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:id" element={<QuotePage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings/*" element={<SettingsPage />} />
        <Route path="*" element={<EmptyState title="העמוד לא נמצא" />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
        <Toaster dir="rtl" position="bottom-left" richColors closeButton toastOptions={{ style: { fontFamily: 'var(--font-sans)' } }} />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
